import _ from 'lodash'

import { clone, curry, setWith } from 'lodash/fp'


const EPSILON = 0.00000001

export const enshureArray = value => (Array.isArray(value) ? value : [value])

export const areEqual = (one, other, epsilon = EPSILON) =>
  Math.abs(one - other) < epsilon

export const equitablesDiff = (one, other) =>
  one.filter(
    oneInstance =>
      !other.find(otherInstance => {
        try {
          return oneInstance.equalTo(otherInstance)
        } catch (_) {
          return false
        }
      })
  )

// equitable - object with equalTo(other) method
export const haveSameEquitables = (one, other) =>
  equitablesDiff(one, other).length === 0 && one.length === other.length


export const getUniqueEquitables = equitables =>
  equitables.reduce(
    (unique, equitable) =>
      unique.find(e => e.equalTo(equitable)) ? unique : [...unique, equitable],
    []
  )

export const between = (value, min, max) => value > min && value < max
export const strictlyBetween = (value, min, max) => value >= min && value <= max

export const toDegrees = radians => (radians * 180) / Math.PI
export const toRadians = degrees => (degrees * Math.PI) / 180

export const callIf = (condition, func) => (condition ? func : _ => null)

export const getUniqueName = (name, otherNames) => {
  const suffixes = otherNames
    // eslint-disable-next-line lodash/prefer-lodash-method
    .filter(n => n.startsWith(name))
    .map(n => n.slice(name.length))
  if (!suffixes.includes('')) return name

  let number = 1
  while (number) {
    const strNumber = number.toString()
    if (!suffixes.includes(strNumber)) return name + strNumber
    number++
  }
}

export const takeFromState = (state, stateObjectName, fields) =>
  _.pick(state[stateObjectName], fields)

export const setIn = curry((obj, path, value) =>
  setWith(clone, path, value, clone(obj))
)

export const firstUpperWords = (text, length) =>
  text
    .split(/\s+/)
    .filter(x => !!x)
    .map(element => element[0].toUpperCase())
    .splice(0, length)

export const sliceWithDots = (text, length) => {
  return text.length > length ? `${text.slice(0, length)  }...` : text
}

export const pluralize = (text, amount) => {
  return amount === 1 ? text : `${text  }s`
}

export const pascalToText = text =>
  _.capitalize(text.replace(/([A-Z][a-z])/g, ' $1').replace(/(\d)/g, ' $1'))

export const pascalToUpperWords = text =>
  pascalToText(text).replace(/\b\w/g, l => l.toUpperCase())

export const snakeToText = text => _.capitalize(text.split('_').join(' '))
export const def = v => v !== undefined

export const logArgs = func => (...args) => {
  // eslint-disable-next-line no-console
  console.info(`${func.name} args: `, ...args)
  return func(...args)
}

export const noPropogation = func => e => {
  e.stopPropagation()
  func(e)
}

export const getWatchingHandlers = (handlers, updaters) => {
  const readyUpdaters = updaters.map(([updater, actions]) => [
    updater,
    actions.map_('toString')
  ])
  return Object.entries(handlers).reduce((acc, [key, handler]) => {
    const watchingFunc = (...args) => {
      const newState = handler(...args)
      return readyUpdaters.reduce((newState, [updater, actions]) => {
        return actions.includes(key) ? updater(newState) : newState
      }, newState)
    }
    return {
      ...acc,
      [key]: watchingFunc
    }
  }, {})
}

export function getImageDimensions(url) {
  return new Promise(function(resolved, rejected) {
    const i = new Image()
    i.onload = function() {
      resolved({ width: i.width, height: i.height })
    }
    i.src = url
    i.onerror = function(err) {
      rejected(err)
    }
  })
}

// https://en.wikipedia.org/wiki/Letter_case#Special_case_styles
export const kebabToCamelCase = text => {
  const splitKebab = text.split('-')
  return splitKebab.map(e => _.capitalize(e)).join('')
}

export const pathnameToPage = pathname => {
  const splitUrl = pathname.split('/')
  return kebabToCamelCase(splitUrl[splitUrl.length - 1])
}

export const addToPath = (path, tail, delimeter = '/') =>
  new RegExp(`.*\\${delimeter}$`).test(path) || !tail
    ? path + tail
    : path + delimeter + tail

export const pathWithQueries = (path, queries) =>
  addToPath(path, new URLSearchParams(queries).toString(), '?')

export const getElementPosition = e => e.currentTarget.getBoundingClientRect()

export const getRandomColor = () =>
  `#${
  Math.random()
    .toString(16)
    .slice(-6)}`

// export const noDefault = func => e => {
//   e.preventDefault()
//   func(e)
// }

export const preventDefaultIfFalse = (prevent, e) => {
  if (!prevent) {
    e.preventDefault()
  }
}

// TODO: RESOLVE
// export const reportError = (message, info) => {
//   if (!LOCAL) {
//     Sentry.withScope(scope => {
//       try {
//         Object.keys(info).forEach(key => {
//           scope.setExtra(key, info[key])
//         })
//       } catch (err) {
//         scope.setExtra('info', info)
//       }
//       Sentry.captureException(message)
//     })
//   } else {
//     console.error(message, info)
//   }
// }

export const getPairs = array =>
  array.length < 2
    ? []
    : array.length === 2
    ? [array]
    : array.map((item, index) =>
        index === array.length - 1 ? [item, array[0]] : [item, array[index + 1]]
      )

export const getListIdx = (str, substr) => {
  const listIdx = []
  let lastIndex = -1
  while ((lastIndex = str.indexOf(substr, lastIndex + 1)) !== -1) {
    listIdx.push(lastIndex)
  }
  return listIdx
}

export const getSplitStr = (str, substr) => {
  const lowStr = str.toLowerCase()
  const lowSubstr = substr.toLowerCase()
  const listIdx = getListIdx(lowStr, lowSubstr)

  if (listIdx.length) {
    const splitText = listIdx.map((item, index) =>
      index === 0
        ? [
            str.slice(0, item),
            str.slice(item, item + substr.length),
            str.slice(item + substr.length, listIdx[index + 1])
          ]
        : [
            str.slice(item, item + substr.length),
            str.slice(item + substr.length, listIdx[index + 1])
          ]
    )
    return splitText.flat()
  } else {
    return [str]
  }
}

export const deleteOnceElementsFromArray = (originArray, deleteArray) => {
  return deleteArray.reduce((acc, id) => {
    const index = acc.indexOf(id)
    if (index > -1) {
      // this step for delete only one element from array
      return acc.slice(0, index).concat(acc.slice(index + 1))
    } else return acc
  }, originArray)
}

export const getElementFromIndex = (array, index) => {
  if (array.length > index) return array[index]
  else return array[index % array.length]
}

export const getWithNewField = (argumentsList, Class) =>
  function(name, value) {
    const args = argumentsList.map(argument =>
      argument === name ? value : this[argument]
    )
    return new Class(...args)
  }

export const getWithNewFieldObject = (argumentsList, Class) =>
  function(name, value) {
    const args = argumentsList.reduce((acc, argument) => {
      return {
        ...acc,
        [argument]: argument === name ? value : this[argument]
      }
    }, {})
    return new Class(args)
  }

export const objectWithoutUndefsAndNulls = object =>
  // eslint-disable-next-line lodash/prefer-lodash-method
  Object.keys(object).reduce((acc, key) => {
    if (object[key] !== undefined && object[key] !== null) {
      acc[key] = object[key]
    }
    return acc
  }, {})

export const getRandomNumber = (lower, upper, float) => {
  const number = _.random(lower, upper, float ? true : false)
  return float ? _.round(number, float) : number
}

export const range = (start, stop, step, multiplier = 1) => {
  const totalSteps = Math.floor((stop - start) / step) + 1

  return Array.from(Array(totalSteps).keys()).reduce((acc, item) => {
    return acc.concat(_.round(item * step + start, 1) * multiplier)
  }, [])
}

export const getNumberWithSpaces = number => {
  if (typeof number === 'number') {
    return String(_.round(number, 3)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    // old method .split(/(?=(?:...)*$)/).join(' ')
  }
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export const mergeCustomizer = (objValue, srcValue) => {
  if (_.isArray(objValue)) {
    return objValue.concat(srcValue)
  }
}

export const additionCustomizer = (objValue, srcValue) => {
  if (Number.isInteger(objValue)) {
    return objValue + srcValue
  }
}

export const convertUnitFormat = unit =>
  unit.slice(-1) === '2'
    ? `${unit.slice(0, -1)}²`
    : unit.slice(-1) === '3'
    ? `${unit.slice(0, -1)}³`
    : unit.slice(-3) === 'pct'
    ? `${unit.slice(0, -3)}%`
    : unit

export const getUniqueString = () =>
  Math.random()
    .toString(36)
    .substring(7)

export const roundBy = (num, dec) => Math.round(num * dec) / dec

export const getRandomByRange = (min, max) => Math.random() * (max - min) + min

export const getFloorRandomByRange = (min, max, numsAfterDecimal = 0) => {
  const random = getRandomByRange(min, max)
  const decimal = numsAfterDecimal * 10 || 1
  return Math.floor(random * decimal) / decimal
}

export const addNumberSpacesToReportsTable = data => {
  Object.entries(data).map(e =>
    (e[1].rows ? e[1].rows : e[1]).map((el, i) =>
      Object.entries(el).map(
        element =>
          typeof element[1] === 'number' &&
          element[0] !== 'elementID' &&
          (data[e[0]].rows[i][element[0]] = getNumberWithSpaces(element[1]))
      )
    )
  )
}

export const copyAddressLink = url => {
  const el = document.createElement('input')
  el.value = url || window.location.href
  el.id = 'url-input'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  el.remove()
}

export const cleanObjectEmptyString = form =>
  Object.fromEntries(
    Object.entries(form).filter(([, value]) => String(value).length > 0)
  )

export const createMetaTags = data => {
  data.forEach(el => {
    const newMetaTag = document.createElement('meta')
    Object.entries(el).forEach(([attribute, value]) => {
      newMetaTag.setAttribute(attribute, value)
    })
    document.getElementsByTagName('head')[0].appendChild(newMetaTag)
  })
}

export const removeMetaTags = data => {
  const attribute = data[0].name ? 'name' : 'property'
  const attributesForDelete = data.map(el => el[attribute])
  const metaTags = [...document.querySelectorAll('meta')]
  metaTags.forEach(
    meta =>
      meta.attributes[attribute] &&
      attributesForDelete.includes(meta.attributes[attribute].value) &&
      meta.remove()
  )
}

export const moveElementToLastPosition = (array, value) => {
  const elementIndex = array.indexOf(value)
  if (elementIndex < 0) return array
  return array.swap_(elementIndex, array.length - 1)
}

export const getClosestNumber = (numbers, value) =>
  numbers.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  )

export const getScreenWidthByPercent = percent =>
  window.innerWidth * (percent / 100)

export const changeObjectValueInArray = (arr, func) =>
  arr.map(obj =>
    Object.entries(obj).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: func(value)
      }
    }, {})
  )

export const roundingInSteps = (value, step) => {
  const inv = 1.0 / step
  return Math.round(value * inv) / inv
}

export const testWebPSupport = callback => {
  const webP = new Image()
  webP.src =
    'data:image/webp;base64,UklGRi4AAABXRUJQVlA4TCEAAAAvAUAAEB8wAiMwAgSSNtse/cXjxyCCmrYNWPwmHRH9jwMA'
  webP.onload = webP.onerror = () => {
    callback(webP.height === 2)
  }
}

export const findFlatIndexInArraysOfArray = (arr, index) => {
  let currentFlatIndex = 0
  for (const [ind, array] of arr.entries()) {
    currentFlatIndex += array.length
    if (index < currentFlatIndex) return ind
  }
  return -1
}

export const generateRandomString = length =>
  Math.random()
    .toString(36)
    .substr(2, length)

export const checkImageExists = imageSrc => {
  let imageUrl
  const img = new Image()
  img.src = imageSrc
  img.onload = imageUrl = imageSrc
  return imageUrl
}
